import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  NgZone,
  OnInit,
  Output
} from '@angular/core';
import { ConfigService } from '@app-core/services';

@Component({
  selector: 'app-recaptcha',
  template: '',
  styleUrls: ['./recaptcha.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class RecaptchaComponent implements OnInit {
  @Output() resolved = new EventEmitter<string>();

  configService = inject(ConfigService);
  environment = this.configService.environment;
  siteKey = this.environment.recaptchaId;

  private zone = inject(NgZone);
  private scriptLoaded = false;

  ngOnInit() {
    if (!this.siteKey) {
      console.error('reCAPTCHA site key is not configured');
      return;
    }

    this.loadScript()
      .then(() => {
        this.executeToken();
      })
      .catch((error) => {
        console.error('reCAPTCHA v3 initialization failed:', error);
        console.error(
          'Note: reCAPTCHA v3 requires a v3 site key. Please ensure you have created a reCAPTCHA v3 site key in Google reCAPTCHA Admin Console and updated your configuration.'
        );
      });
  }

  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.scriptLoaded || window['grecaptcha']) {
        resolve();
        return;
      }

      if (document.querySelector(`script[src*="recaptcha/api.js?render=${this.siteKey}"]`)) {
        const checkInterval = setInterval(() => {
          if (window['grecaptcha']) {
            clearInterval(checkInterval);
            this.scriptLoaded = true;
            resolve();
          }
        }, 100);

        setTimeout(() => {
          clearInterval(checkInterval);
          if (window['grecaptcha']) {
            this.scriptLoaded = true;
            resolve();
          } else {
            reject(new Error('reCAPTCHA script failed to load. Please verify your site key is a reCAPTCHA v3 key.'));
          }
        }, 10000);
        return;
      }

      const script = document.createElement('script');
      script.src = `https://www.google.com/recaptcha/api.js?render=${this.siteKey}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        this.scriptLoaded = true;
        resolve();
      };
      script.onerror = () => {
        reject(
          new Error(
            'Failed to load reCAPTCHA script. The site key may be invalid or not a reCAPTCHA v3 key. Please verify your site key in Google reCAPTCHA Admin Console.'
          )
        );
      };
      document.head.appendChild(script);
    });
  }

  execute(): void {
    this.executeToken();
  }

  private executeToken(): void {
    if (window['grecaptcha'] && window['grecaptcha'].ready) {
      window['grecaptcha'].ready(() => {
        window['grecaptcha']
          .execute(this.siteKey, { action: 'submit' })
          .then((token: string) => {
            this.zone.run(() => this.resolved.emit(token));
          })
          .catch((error: Error) => {
            console.error('reCAPTCHA execution failed:', error);
          });
      });
    } else if (window['grecaptcha']) {
      window['grecaptcha']
        .execute(this.siteKey, { action: 'submit' })
        .then((token: string) => {
          this.zone.run(() => this.resolved.emit(token));
        })
        .catch((error: Error) => {
          console.error('reCAPTCHA execution failed:', error);
        });
    } else {
      console.error('reCAPTCHA not available');
    }
  }
}
